// SPDX-License-Identifier: GPL-2.0-or-later
/**
 * Fetch + cache for the tiered rule catalogue - three levels ( catalog index, one
 * index per rule set, one detail file per rule ), reachable at EITHER of two roots with
 * the same shape: `libs/einvoice-reference-data/dist/rule-catalog/` (bundled, same-origin, no
 * third-party network - the Technical Details plugin's own copy) or `site-api/v1/rules*` (the
 * public reference API on invoicepress.de). One component, one attribute `src`: this class is that one
 * code path; only `baseUrl` differs between hosts.
 *
 * ⚠️ INDEX SHAPE NORMALISED, NOT ASSUMED IDENTICAL - `dist/rule-catalog/index.json` is
 * `{generatedAt,totalRules,standards:[...]}` (build-rule-catalog.php), `site-api/v1/rules.json` is a
 * bare `[...]` array (build-site-api.php, the older, already-versioned contract). Both list the
 * same per-entry shape (`standard`, `syntax`, `appliesTo`, `ruleCount`, plus a `file`/`url` field
 * this class does NOT rely on - the standard's own rule set index/detail paths are derived
 * deterministically from `standard`, never read back from the index). `index()` below accepts
 * either wrapper.
 */

export class RuleCatalogClient {

	/**
	 * ⚠️ A SECOND ROOT FOR THE RULE SETS OF THE EXTENSIONS. Technical Details
	 * does not carry FACTUR-X-EXTENDED and EXTENDED-CTC-FR; the "Extended Profiles" add-on carries
	 * them under `data/protected/rule-catalog/`, in the same layout. index() appends the entries of that
	 * root's own index.json and remembers, per rule set, which root it came from; standard() and rule()
	 * then fetch from there. A missing or unreadable second root is the normal case without the add-on
	 * and is skipped silently - it must never cost the fourteen rule sets of the first root.
	 *
	 * @param {string} baseUrl           e.g. ".../dist/rule-catalog" or ".../site-api/v1/rules" (no trailing slash)
	 * @param {string} [protectedBaseUrl] the add-on's rule-catalog folder, '' without the add-on
	 */
	constructor( baseUrl, protectedBaseUrl = '' ) {

		this._baseUrl = baseUrl.replace( /\/$/, '' );

		this._protectedBaseUrl = ( protectedBaseUrl || '' ).replace( /\/$/, '' );

		/** @type {Map<string, string>} standard slug (lowercase) -> the root it is fetched from */
		this._bases = new Map();

		/** @type {Array<object>|null} */
		this._index = null;

		/** @type {Map<string, Promise<object>>} standard slug (lowercase) -> regelwerk-index */
		this._standards = new Map();

		/** @type {Map<string, Promise<object>>} "standardSlug/id" -> full detail */
		this._rules = new Map();

	}

	/**
	 * Defaults to the 17 in-scope standards. Pass
	 * includeParked = true for an explicit developer switch - only meaningful against the BUNDLED
	 * dist/rule-catalog/ root, which still carries the three parked standards (ZATCA, CIUS-TR,
	 * CIUS-RS) under index-all.json; the public site-api/v1/rules.json root has no such file, since
	 * that build skips parked standards entirely (no rules/ZATCA* etc. path may exist there
	 * at all) - requesting includeParked against a site-api baseUrl 404s, same as any other missing file.
	 *
	 * @param {boolean} [includeParked=false]
	 * @return {Promise<Array<{standard:string,syntax:?string,appliesTo:object,ruleCount:number}>>}
	 */
	async index( includeParked = false ) {

		const cacheKey = includeParked ? 'all' : 'default';

		if ( this._index && this._indexKey === cacheKey )
			return this._index;

		const file = includeParked ? 'index-all.json' : 'index.json';
		const res = await fetch( `${ this._baseUrl }/${ file }` );

		if ( ! res.ok )
			throw new Error( `RuleCatalogClient: ${ file } failed (${ res.status })` );

		const data = await res.json();

		const entries = Array.isArray( data ) ? data : ( data.standards ?? [] );

		/*
		 * ⚠️ The add-on's folder has no index-all.json; it carries no parked rule set. Its index.json
		 * is read for both keys. Entries the first root already lists are not taken twice.
		 */
		if ( this._protectedBaseUrl !== '' ) {

			try {

				const extra = await fetch( `${ this._protectedBaseUrl }/index.json` );

				if ( extra.ok ) {

					const more  = await extra.json();
					const known = new Set( entries.map( ( e ) => String( e.standard ).toLowerCase() ) );

					for ( const e of ( Array.isArray( more ) ? more : ( more.standards ?? [] ) ) ) {

						const key = String( e.standard ?? '' ).toLowerCase();

						if ( key === '' || known.has( key ) )
							continue;

						entries.push( e );
						this._bases.set( key, this._protectedBaseUrl );

					}

				}

			} catch ( e ) {
				// Without the add-on, or with a damaged index of it: the first root alone.
			}

		}

		this._index = entries;
		this._indexKey = cacheKey;

		return this._index;

	}

	/**
	 * The root a rule set is fetched from.
	 *
	 * @param {string} key standard slug, lowercase
	 * @return {string}
	 */
	_baseFor( key ) {
		return this._bases.get( key ) ?? this._baseUrl;
	}

	/**
	 * One standard's regelwerk-index - {standard, syntax, appliesTo, rules:[{id,level,kind,family,
	 * jurisdiction,text,fields?}, ...]}. Cached per standard for the life of this client instance.
	 *
	 * @param {string} standard exact name as index() lists it, e.g. "XRechnung-UBL"
	 * @return {Promise<object>}
	 */
	async standard( standard ) {

		const key = standard.toLowerCase();

		if ( this._standards.has( key ) )
			return this._standards.get( key );

		const promise = fetch( `${ this._baseFor( key ) }/${ key }.json` )
			.then( ( res ) => {
				if ( ! res.ok ) throw new Error( `RuleCatalogClient: ${ key }.json failed (${ res.status })` );
				return res.json();
			} );

		this._standards.set( key, promise );

		return promise;

	}

	/**
	 * One rule's full detail (context/test/description/provenance/duplicates/...). Loaded once,
	 * cached - never call this for a whole standard's rows at once, that is exactly what
	 * `standard()`'s short regelwerk-index is for.
	 *
	 * @param {string} standard
	 * @param {string} id
	 * @return {Promise<object>}
	 */
	async rule( standard, id ) {

		const stdKey = standard.toLowerCase();
		const key = `${ stdKey }/${ id }`;

		if ( this._rules.has( key ) )
			return this._rules.get( key );

		/*
		 * ⚠️ ONE DETAIL FILE PER RULE SET, `<standard>.details.json`, NOT ONE FILE PER RULE ( since 02.11.2026,
		 * `build-rule-catalog.php` ). It is loaded at the first rule opened from that set and kept; the largest set is
		 * 139 KB over the network compressed. The single files made 19,248 files on the server.
		 */
		this._sets = this._sets || new Map();

		if ( ! this._sets.has( stdKey ) )
			this._sets.set( stdKey, fetch( `${ this._baseFor( stdKey ) }/${ stdKey }.details.json` )
				.then( ( res ) => {
					if ( ! res.ok ) throw new Error( `RuleCatalogClient: ${ stdKey }.details.json failed (${ res.status })` );
					return res.json();
				} )
				.then( ( data ) => ( data && data.rules ) || {} ) );

		const promise = this._sets.get( stdKey ).then( ( rules ) => {
			if ( ! rules[ id ] ) throw new Error( `RuleCatalogClient: rule ${ id } not in ${ standard }` );
			return rules[ id ];
		} );

		this._rules.set( key, promise );

		return promise;

	}

}
